import { useEffect, useRef, useState } from 'react'
import store, { RootState } from '../../store'
import { selectActiveLayout, selectActiveLayoutId } from '../../selectors/home'
import { selectUserDashboards } from '../../selectors/layout'
import { selectAllLayouts } from '../../selectors/widgetDashboard'
import { selectChartConfigs, selectDrawings } from '../../selectors/chart'
import { areDashboardsLoaded, selectAutoSave } from '../../selectors/user'
import { selectMultiPaneFullScreen } from '../../selectors/common'
import { selectIsMobile } from '../../selectors/device'
import { getChartConfigsForLayout } from '../../hooks/useChartConfigs'
import { isLayoutChanged } from '../../util/widgetDashboard'
import {
    DashboardShareStatus,
    dashboardShareStatusFromJSON,
    Platform,
    UserDashboardProto
} from '../../proto/generated/user-preference'

export type LayoutMenuRuntime = {
    activeLayoutId: string
    activeDashboard: UserDashboardProto | null
    dashboardsLoaded: boolean
    isDashboardChanged: boolean
    isSavedLayout: boolean
    isUnsavedActiveLayout: boolean
    isViewer: boolean
    shareLayout: boolean
    autoSave: boolean
    platform: Platform
    dashboards: UserDashboardProto[]
    multiPaneFullScreen: boolean
}

/**
 * The autosave timer calls this every second. The comparison serialises and key-sorts the whole
 * layout, so remember the inputs (all immutable store slices) and only redo it when one changed.
 */
let lastDashboardChangedInputs: unknown[] | null = null
let lastDashboardChangedResult = false

export function computeIsDashboardChanged(state: RootState): boolean {
    const activeLayoutId = selectActiveLayoutId(state)
    const activeLayout = selectActiveLayout(state)
    if (!activeLayout) return false

    const dashboards = selectUserDashboards(state)
    const inputs = [activeLayoutId, activeLayout, dashboards, selectAllLayouts(state), selectChartConfigs(state), selectDrawings(state)]
    const prev = lastDashboardChangedInputs
    if (prev && prev.length === inputs.length && prev.every((v, i) => v === inputs[i])) {
        return lastDashboardChangedResult
    }
    const result = computeIsDashboardChangedUncached(state, activeLayoutId, activeLayout, dashboards)
    lastDashboardChangedInputs = inputs
    lastDashboardChangedResult = result
    return result
}

function computeIsDashboardChangedUncached(
    state: RootState,
    activeLayoutId: ReturnType<typeof selectActiveLayoutId>,
    activeLayout: NonNullable<ReturnType<typeof selectActiveLayout>>,
    dashboards: ReturnType<typeof selectUserDashboards>
): boolean {
    const activeDashboard = dashboards.find((d) => d.id === activeLayoutId)
    if (!activeDashboard?.name) return true

    const savedDashboard = dashboards.find((d) => d.id === activeLayoutId)
    const layouts = selectAllLayouts(state)
    const unsavedLayouts = Object.keys(layouts).filter((l) => !dashboards.find((d) => d.id === l))
    if (!savedDashboard) {
        return !!unsavedLayouts.find((id) => id === activeLayoutId)
    }

    const chartConfigs = getChartConfigsForLayout(
        activeLayoutId,
        activeLayout,
        selectChartConfigs(state)
    )
    const drawings = selectDrawings(state)
    return isLayoutChanged(activeLayoutId, activeLayout, savedDashboard, chartConfigs, drawings)
}

function computeRuntime(): LayoutMenuRuntime {
    const state = store.getState()
    const activeLayoutId = selectActiveLayoutId(state)
    const dashboards = selectUserDashboards(state)
    const activeDashboard = dashboards.find((d) => d.id === activeLayoutId) ?? null
    const statusEnum = dashboardShareStatusFromJSON(activeDashboard?.shareState)
    const layouts = selectAllLayouts(state)
    const isSavedLayout = !!dashboards.find((d) => d.id === activeLayoutId)
    const isUnsavedActiveLayout = !!layouts[activeLayoutId] && !isSavedLayout

    return {
        activeLayoutId,
        activeDashboard,
        dashboardsLoaded: areDashboardsLoaded(state),
        isDashboardChanged: computeIsDashboardChanged(state),
        isSavedLayout,
        isUnsavedActiveLayout,
        isViewer: statusEnum === DashboardShareStatus.VIEWER,
        shareLayout:
            statusEnum === DashboardShareStatus.OWNER ||
            statusEnum === DashboardShareStatus.VIEWER,
        autoSave: selectAutoSave(state),
        platform: selectIsMobile(state) ? Platform.MOBILE : Platform.DESKTOP,
        dashboards,
        multiPaneFullScreen: !!selectMultiPaneFullScreen(state)
    }
}

function runtimeEqual(prev: LayoutMenuRuntime, next: LayoutMenuRuntime): boolean {
    return (
        prev.activeLayoutId === next.activeLayoutId &&
        prev.activeDashboard === next.activeDashboard &&
        prev.dashboardsLoaded === next.dashboardsLoaded &&
        prev.isDashboardChanged === next.isDashboardChanged &&
        prev.isSavedLayout === next.isSavedLayout &&
        prev.isUnsavedActiveLayout === next.isUnsavedActiveLayout &&
        prev.isViewer === next.isViewer &&
        prev.shareLayout === next.shareLayout &&
        prev.autoSave === next.autoSave &&
        prev.platform === next.platform &&
        prev.dashboards === next.dashboards &&
        prev.multiPaneFullScreen === next.multiPaneFullScreen
    )
}

/** Snapshot/order-book ticks do not invalidate this hook — only layout-relevant store slices do. */
export function useLayoutMenuRuntime(): LayoutMenuRuntime {
    const [runtime, setRuntime] = useState(() => computeRuntime())
    const runtimeRef = useRef(runtime)

    useEffect(() => {
        const sync = () => {
            const next = computeRuntime()
            // Never call setState with an unchanged value from a store subscription: React keeps every
            // such no-op update queued on the hook until the component next renders, and with ticks
            // arriving ~10 times a second these queues grew by tens of MB per hour.
            if (runtimeEqual(runtimeRef.current, next)) return
            runtimeRef.current = next
            setRuntime(next)
        }
        sync()
        return store.subscribe(sync)
    }, [])

    return runtime
}

export function getLayoutMenuActionContext(state: RootState = store.getState()) {
    const activeLayoutId = selectActiveLayoutId(state)
    const activeLayout = selectActiveLayout(state)
    const layouts = selectAllLayouts(state)
    const configs = selectChartConfigs(state)
    const chartConfigs = getChartConfigsForLayout(activeLayoutId, activeLayout, configs)
    const drawings = selectDrawings(state)
    const dashboards = selectUserDashboards(state)
    const activeDashboard = dashboards.find((d) => d.id === activeLayoutId) ?? null

    return {
        activeLayoutId,
        activeLayout,
        layouts,
        chartConfigs,
        drawings,
        dashboards,
        activeDashboard
    }
}
